import { InputAdornment, type InputProps, TextField, type TextFieldProps } from '@mui/material';
import { type ChangeEvent, useState } from 'react';
import { InfoSolidError } from '@/components/Icons/SystemIcons';
import { formatPercentage } from '@/utils';

interface PercentageInputProps extends Omit<TextFieldProps, 'value' | 'onChange' | 'inputProps'> {
    value?: string | number;
    onChange?: (value: string | number) => void;
    label?: string;
    inputProps?: InputProps;
    tabindex?: string | number;
    allowDecimal?: boolean;
    showErrorAdornment?: boolean;
}

function PercentageInputIcon() {
    return (
        <svg
            fill="#000000"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 394.4 394.4"
        >
            <g>
                <path
                    d="M37.4,377.4c-5.223,0-10.438-1.992-14.423-5.977c-7.97-7.963-7.97-20.883,0-28.846l319.6-319.601
		c7.97-7.97,20.876-7.97,28.846,0c7.97,7.962,7.97,20.882,0,28.845l-319.6,319.601C47.838,375.408,42.623,377.4,37.4,377.4z
		 M394.4,299.199c0-52.496-42.704-95.199-95.2-95.199S204,246.703,204,299.199s42.704,95.201,95.2,95.201
		S394.4,351.695,394.4,299.199z M353.601,299.199c0,29.996-24.405,54.4-54.4,54.4s-54.4-24.404-54.4-54.4
		c0-29.994,24.405-54.398,54.4-54.398S353.601,269.205,353.601,299.199z M190.4,95.2C190.4,42.704,147.696,0,95.2,0S0,42.704,0,95.2
		s42.704,95.2,95.2,95.2S190.4,147.696,190.4,95.2z M149.6,95.2c0,29.995-24.405,54.4-54.4,54.4s-54.4-24.405-54.4-54.4
		s24.405-54.4,54.4-54.4S149.6,65.206,149.6,95.2z"
                />
            </g>
        </svg>
    );
}

export default function PercentageInput({
    tabindex,
    label,
    value,
    onChange,
    inputProps,
    allowDecimal = false,
    showErrorAdornment = false,
    ...rest
}: PercentageInputProps) {
    const [isFocused, setIsFocused] = useState(false);

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
        let rawStr = event.target?.value?.toString();
        // Remove preceding zeros
        rawStr = rawStr.replace(/^0+(?=\d)/, '');
        // If allowDecimal is not set then do not allow the user to use the .
        if (!allowDecimal && rawStr.includes('.')) return;
        // Replacing all the commas so we can again format the numbers
        const resultStr = `${rawStr?.replace(/,/g, '')}%`;
        onChange?.(formatPercentage(resultStr) || formatPercentage(0));
    };

    return (
        <TextField
            {...rest}
            label={label}
            value={formatPercentage(value?.toString())}
            onChange={handleInputChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            InputProps={{
                ...inputProps,
                tabIndex: tabindex ? parseInt(`${tabindex}`, 10) : undefined,
                endAdornment: (
                    <>
                        {!isFocused && (
                            <InputAdornment position="end">
                                {<PercentageInputIcon />}
                            </InputAdornment>
                        )}
                        {inputProps?.endAdornment ||
                            (rest.error && showErrorAdornment && (
                                <InputAdornment position="end">
                                    <InfoSolidError />
                                </InputAdornment>
                            ))}
                    </>
                ),
            }}
        />
    );
}
